import {evaluationIsVariant} from '@homeaway/react-experiment';
import {createSelector} from 'reselect';
import {getIsMobile} from './system';
import {getTravelerSegments} from './travelerSegmentFeatures';
import {getMultiUnitsLayoutValues} from './multiUnits';
import {VRBO_WEB_PDP_SPACES} from '../abacus/experiments';
import {getAbacusTests} from './store';

export const MAIN_CONTENT_SECTIONS = {
    amenities: 'amenities',
    map: 'map',
    overview: 'overview',
    rates_availability: 'rates-availability',
    reviews: 'reviews',
    other_units: 'other-units',
    policies: 'policies-nav',
    roomsAndSpaces: 'rooms-and-spaces',
    ownerManager: 'ownerManager'
};

// All available blocks (desktop and mobile)
export const BLOCKS = {
    ABOUT_THE_PROPERTY: 'about-the-property',
    AMENITIES: 'amenities',
    AUTH_CARD: 'auth-card',
    BEAR_STAY_BANNER: 'bear-stay-banner',
    BOOKING_ASSISTANCE: 'booking-assistance',
    CANCELLATION_POLICY: 'cancellation-policy',
    CHAT_UI: 'chat-ui',
    CIVIL_CODE_DISCLAIMER: 'civil-code-disclaimer',
    CLEANING_PRACTICES: 'cleaning-practices',
    DAMAGE_POLICY: 'damage-policy',
    FREE_CANCELLATION: 'free-cancellation',
    HOUSE_RULES: 'house-rules',
    INLINE_GLOBAL_MESSAGE: 'inline-global-message',
    INLINE_VIRTUAL_TOUR: 'inline-virtual-tour',
    LISTING_DETAILS: 'listing-details',
    LISTING_SUMMARY: 'listing-summary',
    MAP: 'map',
    MIDPAGE_RECO_CAROUSEL: 'midpage-reco-carousel',
    OTHER_UNITS: 'other-units',
    OWNER_DETAILS: 'owner-details',
    OWNER_DETAILS_MINI: 'owner-details-mini',
    POLICIES_HEADLINE: 'policies-headline', // why??
    RATES_AVAILABILITY_CALENDAR: 'rates-availability-calendar',
    REVIEWS: 'reviews',
    RECO_CAROUSELS: 'reco-carousels',
    ROOMS_AND_SPACES: 'rooms-and-spaces',
    TRAVELER_SENTIMENT_HIGHLIGHT: 'traveler-sentiment-highlights',
    TRIP_DETAILS: 'trip-details',
    TRIP_LINK: 'trip-link',
    UNIT_SPACES: 'unit-spaces'
};

const getOtherUnitsSection = ({showMultiUnits, isHotelType}, isMidPage) => {
    return showMultiUnits && isHotelType === isMidPage
        ? // !XOR, return section at midpage for hotels case AND return section at bottom of the page for other listing types
          [
              {
                  name: MAIN_CONTENT_SECTIONS.other_units,
                  elements: [BLOCKS.OTHER_UNITS]
              }
          ]
        : [];
};

export const getRoomsAndSpacesTestOn = createSelector(
    [getAbacusTests],
    (abacusTests) => {
        return evaluationIsVariant(abacusTests[VRBO_WEB_PDP_SPACES], 1);
    }
);

const getDesktopLayout = createSelector(
    [getTravelerSegments, getMultiUnitsLayoutValues, getRoomsAndSpacesTestOn],
    ({coldStartUser}, multiUnitsLayoutValues, roomsAndSpacesTestOn) => {
        return {
            spacingBelow: {
                default: '32px',
                [BLOCKS.INLINE_GLOBAL_MESSAGE]: '24px',
                [BLOCKS.LISTING_SUMMARY]: '0px',
                [BLOCKS.TRIP_DETAILS]: '16px',
                [BLOCKS.ABOUT_THE_PROPERTY]: '64px',
                [BLOCKS.INLINE_VIRTUAL_TOUR]: '64px',
                [BLOCKS.UNIT_SPACES]: '64px',
                [BLOCKS.MIDPAGE_RECO_CAROUSEL]: '64px',
                [BLOCKS.CHAT_UI]: '64px',
                [BLOCKS.OWNER_DETAILS]: '64px',
                [BLOCKS.AMENITIES]: '64px',
                [BLOCKS.POLICIES_HEADLINE]: '0'
            },
            order: [
                {
                    name: MAIN_CONTENT_SECTIONS.overview,
                    elements: [
                        BLOCKS.INLINE_GLOBAL_MESSAGE,
                        BLOCKS.LISTING_SUMMARY,
                        BLOCKS.TRIP_DETAILS,
                        BLOCKS.ABOUT_THE_PROPERTY,
                        BLOCKS.INLINE_VIRTUAL_TOUR,
                        !roomsAndSpacesTestOn ? BLOCKS.UNIT_SPACES : null,
                        coldStartUser && !multiUnitsLayoutValues.showMultiUnits
                            ? BLOCKS.MIDPAGE_RECO_CAROUSEL
                            : null
                    ]
                },
                ...(roomsAndSpacesTestOn
                    ? [
                          {
                              name: MAIN_CONTENT_SECTIONS.roomsAndSpaces,
                              elements: [BLOCKS.ROOMS_AND_SPACES]
                          }
                      ]
                    : []),
                {
                    name: MAIN_CONTENT_SECTIONS.amenities,
                    elements: [BLOCKS.AMENITIES]
                },
                {
                    name: MAIN_CONTENT_SECTIONS.policies,
                    elements: [
                        BLOCKS.POLICIES_HEADLINE,
                        BLOCKS.CANCELLATION_POLICY,
                        BLOCKS.DAMAGE_POLICY,
                        BLOCKS.HOUSE_RULES,
                        BLOCKS.CLEANING_PRACTICES
                    ]
                },
                ...getOtherUnitsSection(multiUnitsLayoutValues, true),
                {
                    name: MAIN_CONTENT_SECTIONS.reviews,
                    elements: [BLOCKS.TRAVELER_SENTIMENT_HIGHLIGHT, BLOCKS.REVIEWS]
                },
                {
                    name: MAIN_CONTENT_SECTIONS.map,
                    elements: [BLOCKS.MAP]
                },
                {
                    name: MAIN_CONTENT_SECTIONS.ownerManager,
                    elements: [BLOCKS.CHAT_UI, BLOCKS.OWNER_DETAILS, BLOCKS.AUTH_CARD]
                },
                {
                    name: MAIN_CONTENT_SECTIONS.rates_availability,
                    elements: [BLOCKS.RATES_AVAILABILITY_CALENDAR]
                },
                ...getOtherUnitsSection(multiUnitsLayoutValues, false)
            ]
        };
    }
);

const getMobileLayout = createSelector(
    [getTravelerSegments, getMultiUnitsLayoutValues, getRoomsAndSpacesTestOn],
    ({coldStartUser}, multiUnitsLayoutValues, roomsAndSpacesTestOn) => {
        return {
            spacingBelow: {
                default: '32px',
                [BLOCKS.LISTING_SUMMARY]: '8px',
                [BLOCKS.TRIP_DETAILS]: '16px',
                [BLOCKS.FREE_CANCELLATION]: '16px',
                [BLOCKS.OWNER_DETAILS_MINI]: '16px',
                [BLOCKS.BOOKING_ASSISTANCE]: '24px',
                [BLOCKS.LISTING_DETAILS]: '56px',
                [BLOCKS.CHAT_UI]: '56px',
                [BLOCKS.OWNER_DETAILS]: '56px',
                [BLOCKS.UNIT_SPACES]: '56px',
                [BLOCKS.AMENITIES]: '56px',
                [BLOCKS.CANCELLATION_POLICY]: '16px',
                [BLOCKS.POLICIES_HEADLINE]: '0'
            },
            order: [
                {
                    name: MAIN_CONTENT_SECTIONS.overview,
                    elements: [
                        BLOCKS.INLINE_GLOBAL_MESSAGE,
                        BLOCKS.LISTING_SUMMARY,
                        BLOCKS.TRIP_DETAILS,
                        BLOCKS.FREE_CANCELLATION,
                        BLOCKS.OWNER_DETAILS_MINI,
                        BLOCKS.BOOKING_ASSISTANCE,
                        BLOCKS.BEAR_STAY_BANNER,
                        BLOCKS.LISTING_DETAILS,
                        BLOCKS.CIVIL_CODE_DISCLAIMER,
                        BLOCKS.INLINE_VIRTUAL_TOUR,
                        coldStartUser && !multiUnitsLayoutValues.showMultiUnits
                            ? BLOCKS.RECO_CAROUSELS
                            : null,
                        !roomsAndSpacesTestOn ? BLOCKS.UNIT_SPACES : null
                    ]
                },
                ...(roomsAndSpacesTestOn
                    ? [
                          {
                              name: MAIN_CONTENT_SECTIONS.roomsAndSpaces,
                              elements: [BLOCKS.ROOMS_AND_SPACES]
                          }
                      ]
                    : []),
                {
                    name: MAIN_CONTENT_SECTIONS.policies,
                    elements: [
                        BLOCKS.POLICIES_HEADLINE,
                        BLOCKS.CANCELLATION_POLICY,
                        BLOCKS.TRIP_LINK,
                        BLOCKS.DAMAGE_POLICY,
                        BLOCKS.HOUSE_RULES,
                        BLOCKS.CLEANING_PRACTICES
                    ]
                },
                ...getOtherUnitsSection(multiUnitsLayoutValues, true),
                {
                    name: MAIN_CONTENT_SECTIONS.reviews,
                    elements: [BLOCKS.TRAVELER_SENTIMENT_HIGHLIGHT, BLOCKS.REVIEWS]
                },
                {name: MAIN_CONTENT_SECTIONS.map, elements: [BLOCKS.MAP]},
                {
                    name: MAIN_CONTENT_SECTIONS.ownerManager,
                    elements: [BLOCKS.CHAT_UI, BLOCKS.OWNER_DETAILS, BLOCKS.AUTH_CARD]
                },
                {
                    name: MAIN_CONTENT_SECTIONS.rates_availability,
                    elements: [BLOCKS.RATES_AVAILABILITY_CALENDAR]
                },
                {name: MAIN_CONTENT_SECTIONS.amenities, elements: [BLOCKS.AMENITIES]},
                ...getOtherUnitsSection(multiUnitsLayoutValues, false)
            ]
        };
    }
);

const getLayout = createSelector(
    [getIsMobile, getDesktopLayout, getMobileLayout],
    (isMobile, desktopLayout, mobileLayout) => {
        return isMobile ? mobileLayout : desktopLayout;
    }
);

export default getLayout;
export {getLayout};
